import React, { useEffect, useMemo, useRef } from "react";
import { useRequest } from "ahooks";
import PaymentType from "@/enum/PaymentType";
import { batchManualAddOrder } from "@/services/api/manualOrder";
import { Button, message, Modal } from "antd";
import OrderDTO from "@/dto/order/OrderDTO";
import useAuthorityCheck from "@/hooks/useAuthorityCheck";
import Authority from "@/enum/Authority";
import { useGlobalStore } from "@/store/hooks";
import { uuid } from "@/utils";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { observer } from "mobx-react-lite";
import Big from "big.js";
import { ManualOrderItem } from "@/scenes/manualOrder/Add/components/OrderItemForm/type";
import CreateDecryptedManualOrderForm, {
    CreateEncryptedManualOrderFormInstance,
} from "./CreateEncryptedManualOrderForm";
import { EncryptedConsigneeInfoFormValues } from "./EncryptedConsigneeInfoForm/type";
import AutoLoadingButton from "@/components/AutoLoadingButton";
import { getOriginalIds, getRealOrderOriginalId, selectAllAvailableOrderItem } from "@/utils/OrderUtils";
import { isPaymentsLessZero, showPaymentsLessZeroModal } from "@/scenes/manualOrder/Add/util";
import PrintWaybillModal from "@/scenes/OrderManagement/components/dialogs/PrintWaybillDialog/PrintWaybillModal";
import { useModalStore } from "@/hooks/Modal/hooks";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

interface CreateEncryptedManualOrderModalProps extends InjectBaseModalProps {
    order: OrderDTO;
    eventContext?: string;
}

const CreateEncryptedManualOrderModal: React.FC<CreateEncryptedManualOrderModalProps> = function (props) {
    const { order, open, onCancel, eventContext } = props;

    const form = useRef<CreateEncryptedManualOrderFormInstance>();

    const { runAsync, loading } = useRequest(batchManualAddOrder, { manual: true });

    const { hasAuthority, openAuthorityModal } = useAuthorityCheck(Authority.ROLE_ORDER_IMPORT);
    const { shopMapping } = useGlobalStore();
    const shop = shopMapping.get(order?.shopId);
    const modalStore = useModalStore();

    async function handleSaveOrder(saveAndPrint = false) {
        const { consignee, orderItems, shopId, shippingAddress } = await form.current.getFormValues();

        // 代收货款
        let paymentType = PaymentType.OFFLINE;

        if (consignee.isCodCheckbox && consignee.codAmounts) {
            paymentType = PaymentType.COD;
        }

        if (paymentType === PaymentType.OFFLINE) {
            const { isLess, orderItemPrice, shippingCost, discounts, payments } = isPaymentsLessZero(
                orderItems,
                consignee.shippingCost,
                consignee.discounts,
            );

            if (isLess) {
                showPaymentsLessZeroModal(orderItemPrice, shippingCost, discounts, payments);

                return null;
            }
        }

        const { shipperName, id: shippingAddressId } = shippingAddress || {};

        const orderDto = {
            ...consignee,
            paymentType,
            orderItems,
            shipperName,
            shippingAddressId,
            originalId: order.originalId,
        };

        const results = await runAsync({ shopId, orders: [orderDto] as any, saveAndPrint });
        const result = results?.[0];

        onCancel();

        collectEvent(DataTrackEvent.CREATE_PLATFORM_MANUAL_ORDER, {
            shop_id: shop.id,
            shop_name: shop.name,
            shop_type: ShopTypeNameMap[shop.type],
            type: "密文",
            context: eventContext,
        });

        if (result?.success && result?.order) {
            message.success("添加成功");

            return result.order;
        } else {
            Modal.error({ title: "保存失败，请联系客服反馈", content: result?.message });

            return null;
        }
    }

    const initialFormValues = useMemo(() => {
        if (!hasAuthority || !shop) return undefined;

        const consigneeInfoFormValues: EncryptedConsigneeInfoFormValues = {
            consigneeName: order.consignee.name,
            mobile: order.consignee.mobile,
            telephone: order.consignee.telephone,
            province: order.consignee.province,
            city: order.consignee.city,
            district: order.consignee.district,
            provinceCode: order.provinceCode,
            cityCode: order.cityCode,
            districtCode: order.districtCode,
            street: order.consignee.street,
            discounts: order.discounts,
            shippingCost: order.shippingFee || 0,
            weight: Big(order.weight || 0)
                .div(1000)
                .toNumber(),
            buyerUsername: order.buyerUsername,
            buyerNickname: order.buyerNickname,
            sellerRemarks:
                (order.sellerRemarks?.localContent ?? order.sellerRemarks?.content ?? "") +
                `原订单编号:${getOriginalIds(order).join()}`,
            buyerRemarks: `原订单编号：${getOriginalIds(order).join()}, 原店铺：${shop.name}`,
            sourceOrderInfo: {
                orderOriginalId: getRealOrderOriginalId(order),
                sellerType: shop.sellerType,
                sellerUsername: shop.sellerUsername,
                shopId: order.shopId,
                shopType: shop.type,
                shopName: shop.name,
            },
        };

        const manualOrderItems: ManualOrderItem[] = [];

        order.orderItems?.forEach((orderItem) => {
            const product: ManualOrderItem = {
                key: uuid(),
                productOriginalId: orderItem.productOriginalId,
                skuOriginalId: orderItem.skuOriginalId,
            };

            product.title = orderItem.title;
            product.price = orderItem.price;
            product.quantity = orderItem.quantity;
            product.skuExternalId = orderItem.skuExternalId;
            product.productExternalId = orderItem.productExternalId;
            product.skuProperties = orderItem.skuProperties;
            product.picture = orderItem.picture;
            product.simpleTitle = orderItem.simpleTitle;

            manualOrderItems.push(product);
        });

        return {
            consignee: consigneeInfoFormValues,
            orderItems: manualOrderItems,
            shipperAddressId: order.sellerRemarks?.shippingAddressId,
        };
    }, [hasAuthority, order, shop]);

    async function handleSaveOrderAndPrint() {
        const manualOrder = await handleSaveOrder(true);

        if (manualOrder) {
            selectAllAvailableOrderItem(manualOrder);

            modalStore.openModal(PrintWaybillModal, {
                orders: [manualOrder],
                onShipTaskDone: () => {},
                defaultTemplateId: null,
                onUpdateOrder: () => {},
                onRefreshOrders: () => {},
                onUpdateSupplierOrderRemarks: () => {},
            });
        }
    }

    useEffect(() => {
        if (hasAuthority === false) {
            openAuthorityModal();
        }
    }, [hasAuthority]);

    return (
        <Modal
            width={1500}
            title="创建手工/售后订单"
            open={open}
            okText="保存订单"
            confirmLoading={loading}
            onCancel={onCancel}
            footer={
                <div className="flex flex-center" style={{ justifyContent: "space-between" }}>
                    <div className="error">
                        说明:1 密文订单不支持查看和修改地址，合并和拆分订单，不支持推送给供货商 2
                        密文订单打印快递单，收货人地址跟随原订单地址，不一定是创建时的密文地址
                    </div>
                    <div>
                        <Button onClick={onCancel} className="mr10">
                            取消
                        </Button>
                        <AutoLoadingButton className="ant-btn-warning mr3" onClick={handleSaveOrderAndPrint}>
                            保存并打印
                        </AutoLoadingButton>
                        <AutoLoadingButton
                            className="ml5"
                            type="primary"
                            onClick={async () => {
                                await handleSaveOrder();
                            }}
                        >
                            保存订单
                        </AutoLoadingButton>
                    </div>
                </div>
            }
        >
            <CreateDecryptedManualOrderForm ref={form} initialValues={initialFormValues} />
        </Modal>
    );
};

export default observer(CreateEncryptedManualOrderModal);
